import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, animate as fmAnimate } from "framer-motion";

// =====================================================================
// SpeedingText
// ---------------------------------------------------------------------
// Text that stretches and smears as it races past the viewport.
//
// Two modes, picked automatically:
//   - COUNTER mode (default): animates a number from `from` -> `value`.
//     The faster the number is moving in a given instant, the more
//     motion-blur + horizontal stretch it gets — so it "smears" hardest
//     mid-run and sharpens back to a crisp, still number at the end.
//   - WORD mode (pass `words`): cycles through a list of words, each one
//     flying in from one side and the previous one flying out the other,
//     both blurred/stretched at the moment of peak speed.
//
// This is an original implementation (not copied from any paid/licensed
// package) written to match the publicly documented prop surface of
// React Bits Pro's "Speeding Text" component, so it can be used as a
// drop-in for that same call shape without needing a license.
// =====================================================================

export interface SpeedingTextProps {
  // ---- Counter mode ----
  value?: number;
  from?: number;
  duration?: number; // ms to animate from -> value
  decimals?: number;
  locale?: string;
  showSeparator?: boolean; // thousands separators (1,000 vs 1000)

  // ---- Word mode ----
  words?: string[];
  interval?: number; // ms each word is held
  swapDuration?: number; // ms a swap transition takes
  travel?: number; // px a swapping word travels

  // ---- Shared ----
  prefix?: string;
  suffix?: string;
  blurStrength?: number; // multiplier on speed -> blur
  maxBlur?: number; // px cap on blur radius
  fontSize?: number;
  fontWeight?: number;
  italic?: boolean;
  textColor?: string;
  backgroundColor?: string;
  align?: "left" | "center" | "right";
  startOnView?: boolean;
  loop?: boolean;
  loopDelay?: number;
  paused?: boolean;
  width?: string | number;
  height?: string | number;
  className?: string;
  textClassName?: string;
  style?: React.CSSProperties;
}

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

// Hold a `start` flag until the element scrolls into view (or fire
// immediately if startOnView is false).
function useStartOnView(enabled: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(!enabled);

  useEffect(() => {
    if (!enabled || started) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, started]);

  return { ref, started };
}

export const SpeedingText: React.FC<SpeedingTextProps> = ({
  value = 20000,
  from = 0,
  duration = 2200,
  decimals = 0,
  locale = "en-US",
  showSeparator = true,

  words,
  interval = 1400,
  swapDuration = 520,
  travel = 90,

  prefix = "",
  suffix = "",
  blurStrength = 1,
  maxBlur = 14,
  fontSize = 96,
  fontWeight = 700,
  italic = true,
  textColor = "#0a0a0a",
  backgroundColor = "transparent",
  align = "center",
  startOnView = true,
  loop = false,
  loopDelay = 900,
  paused = false,
  width = "100%",
  height = "100%",
  className = "",
  textClassName,
  style,
}) => {
  const isWordMode = Array.isArray(words) && words.length > 0;
  const { ref: viewRef, started } = useStartOnView(startOnView);

  const containerStyle: React.CSSProperties = {
    width,
    height,
    backgroundColor,
    display: "flex",
    alignItems: "center",
    justifyContent:
      align === "left"
        ? "flex-start"
        : align === "right"
        ? "flex-end"
        : "center",
    overflow: "hidden",
    ...style,
  };

  // If `textClassName` is supplied, Tailwind owns font-size/weight/style/
  // color (so it inherits your existing design tokens) and we only add
  // the dynamic bits (nowrap + a perf hint). Otherwise fall back to the
  // documented inline-style props.
  const textBaseStyle: React.CSSProperties = textClassName
    ? { whiteSpace: "nowrap", willChange: "transform, filter" }
    : {
        fontSize,
        fontWeight,
        fontStyle: italic ? "italic" : "normal",
        color: textColor,
        whiteSpace: "nowrap",
        willChange: "transform, filter",
      };

  // ------------------------------------------------------------------
  // COUNTER MODE
  // ------------------------------------------------------------------
  const [display, setDisplay] = useState(from);
  const [blur, setBlur] = useState(0);
  const [stretch, setStretch] = useState(1);
  const lastTickRef = useRef({ value: from, time: performance.now() });
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (isWordMode || !started || paused) return;

    let cancelled = false;

    const run = () => {
      // First run starts at `from`; every subsequent run (i.e. the
      // `value` prop changed while already mounted) continues smoothly
      // from wherever the number currently sits, instead of jumping
      // back to `from` each time.
      const startValue = hasRunRef.current ? lastTickRef.current.value : from;
      hasRunRef.current = true;

      lastTickRef.current = { value: startValue, time: performance.now() };
      setDisplay(startValue);

      const runDistance = Math.abs(value - startValue) || 1;

      const controls = fmAnimate(startValue, value, {
        duration: duration / 1000,
        ease: "easeInOut",
        onUpdate: (latest) => {
          const now = performance.now();
          const dt = Math.max(now - lastTickRef.current.time, 1);
          const dv = Math.abs(latest - lastTickRef.current.value);
          // Normalize speed against this run's distance so it behaves
          // consistently whether it's climbing 100 or 2,000,000, and
          // whether it's the initial run or a small incremental update.
          const speed = dv / dt / (runDistance / duration); // ~0..N
          const nextBlur = clamp(speed * maxBlur * blurStrength, 0, maxBlur);
          const nextStretch = 1 + clamp(speed * 0.35, 0, 0.6);

          lastTickRef.current = { value: latest, time: now };
          setDisplay(latest);
          setBlur(nextBlur);
          setStretch(nextStretch);
        },
        onComplete: () => {
          if (cancelled) return;
          setBlur(0);
          setStretch(1);
          if (loop) {
            setTimeout(() => {
              if (!cancelled) run();
            }, loopDelay);
          }
        },
      });

      return controls;
    };

    const controls = run();
    return () => {
      cancelled = true;
      controls?.stop?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isWordMode,
    started,
    paused,
    from,
    value,
    duration,
    loop,
    loopDelay,
    maxBlur,
    blurStrength,
  ]);

  const formattedNumber = useMemo(() => {
    if (isWordMode) return "";
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: showSeparator,
    }).format(display);
  }, [isWordMode, display, locale, decimals, showSeparator]);

  // ------------------------------------------------------------------
  // WORD MODE
  // ------------------------------------------------------------------
  const [wordIndex, setWordIndex] = useState(0);
  const [wordDone, setWordDone] = useState(false);

  useEffect(() => {
    if (!isWordMode || !started || paused || !words) return;
    if (wordDone) return;

    const isLast = wordIndex === words.length - 1;

    const timer = setTimeout(() => {
      if (isLast) {
        if (loop) {
          setTimeout(() => setWordIndex(0), loopDelay);
        } else {
          setWordDone(true);
        }
      } else {
        setWordIndex((i) => i + 1);
      }
    }, interval);

    return () => clearTimeout(timer);
  }, [
    isWordMode,
    started,
    paused,
    wordIndex,
    words,
    interval,
    loop,
    loopDelay,
    wordDone,
  ]);

  // Peak blur/stretch during a swap is a function of how far the word
  // travels vs. how long the swap takes — a fast, short swap smears
  // more than a slow, long one.
  const swapPeakBlur = isWordMode
    ? clamp(
        (travel / Math.max(swapDuration, 1)) * 40 * blurStrength,
        0,
        maxBlur
      )
    : 0;
  const swapPeakStretch = isWordMode
    ? 1 + clamp((travel / Math.max(swapDuration, 1)) * 0.25, 0, 0.6)
    : 1;

  return (
    <div ref={viewRef} className={className} style={containerStyle}>
      {isWordMode ? (
        <div style={{ position: "relative", overflow: "hidden" }}>
          <AnimatePresence mode="popLayout">
            <motion.span
              key={wordIndex}
              className={textClassName}
              style={textBaseStyle}
              initial={{
                x: travel,
                opacity: 0,
                filter: `blur(${swapPeakBlur}px)`,
                scaleX: swapPeakStretch,
              }}
              animate={{
                x: 0,
                opacity: 1,
                filter: "blur(0px)",
                scaleX: 1,
              }}
              exit={{
                x: -travel,
                opacity: 0,
                filter: `blur(${swapPeakBlur}px)`,
                scaleX: swapPeakStretch,
              }}
              transition={{
                duration: swapDuration / 1000,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              {prefix}
              {words![wordIndex]}
              {suffix}
            </motion.span>
          </AnimatePresence>
        </div>
      ) : (
        <span
          className={textClassName}
          style={{
            ...textBaseStyle,
            filter: `blur(${blur}px)`,
            transform: `scaleX(${stretch})`,
            display: "inline-block",
          }}
        >
          {prefix}
          {formattedNumber}
          {suffix}
        </span>
      )}
    </div>
  );
};

export default SpeedingText;
